const POINT_TYPES = new Set(['acc', 'brake', 'coast'])
const MAX_TRACK_RESULTS = 10
const MAX_LINE_POINTS = 5000
const MAX_TRACK_DISTANCE_METERS = 10000
const startsByTrackCache = new WeakMap()

const asNumber = value => {
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

const textResult = data => ({
  content: [{ type: 'text', text: JSON.stringify(data) }],
  structuredContent: data,
})

const toolResultData = result => {
  if (result && result.structuredContent !== undefined) return result.structuredContent
  const text = result?.content?.find(item => item?.type === 'text')?.text
  if (text) return JSON.parse(text)
  return result
}

const locationFromTrack = track => {
  const raw = track?.location
  if (Array.isArray(raw)) return { lat: asNumber(raw[0]), lng: asNumber(raw[1]) }
  return {
    lat: asNumber(raw?.lat ?? raw?.latitude ?? raw?._latitude ?? track?.lat),
    lng: asNumber(raw?.lng ?? raw?.longitude ?? raw?._longitude ?? track?.lng),
  }
}

const normalizeTrack = track => {
  const { lat, lng } = locationFromTrack(track)
  const trackId = track?.objectID || track?.id
  if (!track || !trackId || !track.name || !track.url || lat === null || lng === null) {
    throw new Error('The racetrack search returned an incomplete track record.')
  }
  return {
    trackId: String(trackId),
    name: String(track.name),
    type: String(track.type || 'track'),
    url: String(track.url),
    placeId: String(track.place_id || track.gmaps_place_id || track.placeId || ''),
    lat,
    lng,
    zoom: Number.isFinite(Number(track.zoom))
      ? Number(track.zoom)
      : (track.type === 'racetrack' ? 15 : 19),
  }
}

const distanceMeters = (a, b) => {
  const toRadians = degrees => degrees * Math.PI / 180
  const earthRadius = 6371000
  const latDelta = toRadians(b.lat - a.lat)
  const lngDelta = toRadians(b.lng - a.lng)
  const lat1 = toRadians(a.lat)
  const lat2 = toRadians(b.lat)
  const value = Math.sin(latDelta / 2) ** 2
    + Math.cos(lat1) * Math.cos(lat2) * Math.sin(lngDelta / 2) ** 2
  return earthRadius * 2 * Math.atan2(Math.sqrt(value), Math.sqrt(1 - value))
}

// Match the visual editor: a closed Racing Line needs at least six sampled points.
const isClosed = points => points.length > 5 && distanceMeters(points[0], points[points.length - 1]) <= 30

const normalizePoints = ({ points, center, offset = 0 }) => {
  if (!Array.isArray(points) || points.length === 0 || points.length > MAX_LINE_POINTS) {
    throw new Error(`Provide between 1 and ${MAX_LINE_POINTS} racing line points.`)
  }
  return points.map((point, index) => {
    const lat = asNumber(point?.lat)
    const lng = asNumber(point?.lng)
    if (lat === null || lng === null || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      throw new Error(`Point ${index + 1} must contain a valid latitude and longitude.`)
    }
    if (!POINT_TYPES.has(point?.type)) {
      throw new Error(`Point ${index + 1} type must be acc, brake, or coast.`)
    }
    if (!center || distanceMeters(center, { lat, lng }) > MAX_TRACK_DISTANCE_METERS) {
      throw new Error(`Point ${index + 1} must be within 10 km of the selected racetrack.`)
    }
    return {
      id: `webmcp-${offset + index}`,
      lat,
      lng,
      type: point.type,
      selected: false,
    }
  })
}

const createTrackSelectionTools = ({ searchTracks, startLine, trackCache }) => {
  const selectedTracks = trackCache || new Map()
  const startPromises = startsByTrackCache.get(selectedTracks) || new Map()
  startsByTrackCache.set(selectedTracks, startPromises)
  return [
    {
      name: 'open_racer_search_tracks',
      description: 'Search Open Racer racetracks. Use this before starting a Racing Line and choose one returned trackId.',
      inputSchema: {
        type: 'object',
        properties: {
          query: { type: 'string', minLength: 2, maxLength: 100 },
          limit: { type: 'integer', minimum: 1, maximum: MAX_TRACK_RESULTS, default: 5 },
        },
        required: ['query'],
        additionalProperties: false,
      },
      annotations: { readOnlyHint: true },
      execute: async input => {
        const query = String(input?.query || '').trim()
        const limit = Math.min(MAX_TRACK_RESULTS, Math.max(1, Number(input?.limit) || 5))
        if (query.length < 2) throw new Error('Enter at least two characters to search for a racetrack.')
        const tracks = (await searchTracks({ query, limit })).map(normalizeTrack)
        selectedTracks.clear()
        startPromises.clear()
        tracks.forEach(track => selectedTracks.set(track.trackId, track))
        return textResult({ query, tracks })
      },
    },
    {
      name: 'open_racer_start_racing_line',
      description: 'Select a racetrack returned by open_racer_search_tracks and open a new private Racing Line draft in the editor.',
      inputSchema: {
        type: 'object',
        properties: { trackId: { type: 'string', minLength: 1, maxLength: 1500 } },
        required: ['trackId'],
        additionalProperties: false,
      },
      annotations: { destructiveHint: false },
      execute: async input => {
        const track = selectedTracks.get(String(input?.trackId || ''))
        if (!track) throw new Error('Search for the racetrack first, then use a returned trackId.')
        if (!startPromises.has(track.trackId)) {
          startPromises.set(track.trackId, Promise.resolve().then(() => startLine(track)))
        }
        return textResult({ track, ...(await startPromises.get(track.trackId)) })
      },
    },
  ]
}

const createEditorTools = ({ getState, replacePoints, appendPoints, setDetails, undo, redo, finish }) => {
  const stateResult = () => {
    const state = getState()
    return {
      lineId: state.lineId,
      trackName: state.trackName,
      center: state.center,
      zoom: state.zoom,
      pointCount: state.points.length,
      points: state.points,
      vehicle: state.vehicle,
      visibility: state.visibility,
      editMode: state.editMode,
      isOwner: state.isOwner,
      isCloseDetected: state.isCloseDetected,
    }
  }
  const requireEditable = () => {
    const state = getState()
    if (!state?.lineId || !state.isOwner || !state.editMode) {
      throw new Error('The selected Racing Line must be open in edit mode and owned by the signed-in user.')
    }
    return state
  }
  const actionTool = (name, description, action) => ({
    name,
    description,
    inputSchema: { type: 'object', properties: {}, additionalProperties: false },
    annotations: { destructiveHint: false },
    execute: async () => {
      requireEditable()
      await action()
      return textResult(stateResult())
    },
  })

  const pointsSchema = {
    type: 'array', minItems: 1, maxItems: MAX_LINE_POINTS,
    items: {
      type: 'object',
      properties: {
        lat: { type: 'number', minimum: -90, maximum: 90 },
        lng: { type: 'number', minimum: -180, maximum: 180 },
        type: { type: 'string', enum: ['acc', 'brake', 'coast'] },
      },
      required: ['lat', 'lng', 'type'],
      additionalProperties: false,
    },
  }

  return [
    {
      name: 'open_racer_get_editor_state',
      description: 'Read the selected track, Racing Line points, ownership, editing, closure, vehicle, and visibility state.',
      inputSchema: { type: 'object', properties: {}, additionalProperties: false },
      annotations: { readOnlyHint: true },
      execute: async () => textResult(stateResult()),
    },
    {
      name: 'open_racer_replace_line',
      description: 'Replace the complete Racing Line with ordered GPS points near the selected racetrack.',
      inputSchema: { type: 'object', properties: { points: pointsSchema }, required: ['points'], additionalProperties: false },
      annotations: { destructiveHint: true },
      execute: async input => {
        const state = requireEditable()
        const points = normalizePoints({ points: input?.points, center: state.center })
        const closeDetected = isClosed(points)
        await replacePoints({ points, isCloseDetected: closeDetected })
        return textResult({ pointCount: points.length, isCloseDetected: closeDetected })
      },
    },
    {
      name: 'open_racer_append_points',
      description: 'Append ordered GPS points to the current Racing Line.',
      inputSchema: { type: 'object', properties: { points: pointsSchema }, required: ['points'], additionalProperties: false },
      annotations: { destructiveHint: false },
      execute: async input => {
        const state = requireEditable()
        if (state.points.length + (input?.points?.length || 0) > MAX_LINE_POINTS) throw new Error(`A Racing Line can contain at most ${MAX_LINE_POINTS} points.`)
        const points = normalizePoints({ points: input?.points, center: state.center, offset: state.points.length })
        const combined = [...state.points, ...points]
        const closeDetected = isClosed(combined)
        await appendPoints({ points, isCloseDetected: closeDetected })
        return textResult({ pointCount: combined.length, appendedCount: points.length, isCloseDetected: closeDetected })
      },
    },
    {
      name: 'open_racer_set_line_details',
      description: 'Set the vehicle label and requested public or private visibility for the Racing Line.',
      inputSchema: {
        type: 'object',
        properties: {
          vehicle: { type: 'string', maxLength: 80 },
          visibility: { type: 'string', enum: ['public', 'private'] },
        },
        additionalProperties: false,
      },
      annotations: { destructiveHint: false },
      execute: async input => {
        requireEditable()
        const details = {}
        if (input?.vehicle !== undefined) details.vehicle = String(input.vehicle).trim().slice(0, 80)
        if (input?.visibility !== undefined) {
          if (!['public', 'private'].includes(input.visibility)) throw new Error('Visibility must be public or private.')
          details.visibility = input.visibility
        }
        if (!Object.keys(details).length) throw new Error('Provide a vehicle or visibility to update.')
        await setDetails(details)
        return textResult({ updated: details })
      },
    },
    actionTool('open_racer_undo', 'Undo the most recent Racing Line point edit.', undo),
    actionTool('open_racer_redo', 'Redo the most recently undone Racing Line point edit.', redo),
    {
      name: 'open_racer_finish_line',
      description: 'Save and finish a closed Racing Line. Signed-in owners can publish a public line; anonymous drafts remain private until sign-in.',
      inputSchema: { type: 'object', properties: {}, additionalProperties: false },
      annotations: { destructiveHint: false },
      execute: async () => {
        const state = requireEditable()
        if (!state.isCloseDetected) throw new Error('Close the Racing Line by ending near its first point before finishing.')
        await finish()
        return textResult({ lineId: state.lineId, finished: true, requestedVisibility: state.visibility })
      },
    },
  ]
}

const registerWebMcpTools = (modelContext, tools) => {
  if (!modelContext || typeof modelContext.registerTool !== 'function') return null
  const controller = new AbortController()
  controller.ready = Promise.all(tools.map(tool => modelContext.registerTool(tool, { signal: controller.signal })))
    .catch(error => {
      controller.abort()
      throw error
    })
  return controller
}

module.exports = {
  createTrackSelectionTools,
  createEditorTools,
  registerWebMcpTools,
  toolResultData,
}
